import { useAuth } from "@/contexts/AuthContext";
import { AlertCircle, Clock } from "lucide-react";

/**
 * Shows a trial countdown banner when:
 *   - trial_status === 'active' and trial_days_remaining <= 14 → orange "X days left"
 *   - trial_status === 'expired' → red read-only banner
 * Renders nothing for unlimited/admin users or when no countdown is needed.
 */
export default function TrialBanner() {
  const { user } = useAuth();
  if (!user) return null;
  const { trial_status, trial_days_remaining, trial_ends_at } = user;

  if (trial_status === "expired") {
    return (
      <div
        className="flex items-start gap-3 px-6 lg:px-12 py-3 text-sm"
        style={{ background: "#FEE2E2", color: "#991B1B", borderBottom: "1px solid #FCA5A5" }}
        data-testid="trial-banner-expired"
      >
        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <strong>Your free trial ended on {trial_ends_at?.slice(0, 10)}.</strong>{" "}
          Your books are now read-only. Contact the admin to extend access.
        </div>
      </div>
    );
  }

  if (trial_status === "active" && trial_days_remaining !== null && trial_days_remaining <= 14) {
    return (
      <div
        className="flex items-start gap-3 px-6 lg:px-12 py-3 text-sm"
        style={{ background: "#FEF3C7", color: "#92400E", borderBottom: "1px solid #FCD34D" }}
        data-testid="trial-banner-active"
      >
        <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <strong>{trial_days_remaining} day{trial_days_remaining === 1 ? "" : "s"} left in your free trial.</strong>{" "}
          After {trial_ends_at?.slice(0, 10)} the app switches to read-only. Reach out to extend.
        </div>
      </div>
    );
  }

  return null;
}
